import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";

/**
 * Lightweight, non-recursive collapsible JSON viewer.
 * Renders the JSON as a single formatted string. Top-level keys
 * (e.g. "chapters") can be collapsed by clicking their headers.
 * No self-referential JSX — safe with visual-edits babel plugin.
 */
export default function JsonTree({ data }) {
  const [openKeys, setOpenKeys] = useState(() => {
    const all = {};
    if (data && typeof data === "object" && !Array.isArray(data)) {
      Object.keys(data).forEach((k) => {
        all[k] = true;
      });
    }
    return all;
  });

  const sections = useMemo(() => {
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return [{ key: null, value: data }];
    }
    return Object.entries(data).map(([k, v]) => ({ key: k, value: v }));
  }, [data]);

  const toggle = (k) =>
    setOpenKeys((prev) => ({ ...prev, [k]: !prev[k] }));

  return (
    <div className="space-y-3">
      {sections.map(({ key, value }) => {
        if (key === null) {
          return (
            <pre
              key="root"
              className="whitespace-pre-wrap break-words text-stone-800"
            >
              {JSON.stringify(value, null, 2)}
            </pre>
          );
        }
        const open = openKeys[key];
        const compound =
          value !== null && typeof value === "object";
        const summary = compound
          ? Array.isArray(value)
            ? `Array(${value.length})`
            : `Object(${Object.keys(value).length} keys)`
          : JSON.stringify(value);
        return (
          <div key={key} className="border border-stone-200 rounded-md bg-white">
            <button
              type="button"
              onClick={() => toggle(key)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-stone-50"
              data-testid={`json-section-toggle-${key}`}
            >
              {open ? (
                <ChevronDown className="h-3.5 w-3.5 text-stone-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-stone-500" />
              )}
              <span className="font-medium text-stone-800">{key}</span>
              <span className="text-stone-500 text-xs ml-1">{summary}</span>
            </button>
            {open && (
              <pre
                className="px-3 pb-3 whitespace-pre-wrap break-words text-stone-800 border-t border-stone-100 pt-2"
                data-testid={`json-section-body-${key}`}
              >
                {JSON.stringify(value, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
